import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { CloudOff, SearchX, CircleSlash } from 'lucide-react'
import { buttonVariants, Button } from '@/components/ui/button'
import { Card, IconBadge } from '@/components/ui/card'

/* Spec PWA-001 — estados de sistema (nao encontrado, indisponivel, sem conexao, falha de dados). */

function StatusLayout({ icon, title, body, action }: { icon: ReactNode; title: string; body: string; action?: ReactNode }) {
    return (
        <div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 px-6 py-10 text-center">
            <IconBadge className="size-16">{icon}</IconBadge>
            <h1 className="m-0 font-display text-2xl font-bold">{title}</h1>
            <p className="m-0 max-w-xs text-[0.95rem] leading-relaxed text-ink-muted">{body}</p>
            {action}
        </div>
    )
}

export function NotFoundScreen() {
    const { t } = useTranslation()
    return (
        <StatusLayout
            icon={<SearchX className="size-7" aria-hidden="true" />}
            title={t('sponsorShell.notFound.title')}
            body={t('sponsorShell.notFound.body')}
            action={<Link to="/" className={buttonVariants({ variant: 'primary', size: 'md' })}>{t('sponsorShell.notFound.action')}</Link>}
        />
    )
}

export function UnavailableScreen() {
    const { t } = useTranslation()
    return (
        <main className="min-h-dvh bg-app-background">
            <StatusLayout
                icon={<CircleSlash className="size-7" aria-hidden="true" />}
                title={t('sponsorShell.unavailable.title')}
                body={t('sponsorShell.unavailable.body')}
            />
        </main>
    )
}

export function NetworkErrorScreen({ onRetry }: { onRetry: () => void }) {
    const { t } = useTranslation()
    return (
        <main className="min-h-dvh bg-app-background">
            <StatusLayout
                icon={<CloudOff className="size-7" aria-hidden="true" />}
                title={t('sponsorShell.offline.title')}
                body={t('sponsorShell.offline.body')}
                action={<Button variant="primary" size="md" onClick={onRetry}>{t('sponsorCommon.retry')}</Button>}
            />
        </main>
    )
}

/* Falha ao carregar dados autenticados (CRM indisponivel etc.) — nunca mostra dado antigo alem do cache curto. */
export function DataUnavailableCard({ onRetry }: { onRetry: () => void }) {
    const { t } = useTranslation()
    return (
        <Card role="alert" className="flex flex-col items-start gap-3 p-5">
            <div className="flex items-center gap-3">
                <IconBadge><CloudOff className="size-5" aria-hidden="true" /></IconBadge>
                <h2 className="m-0 font-display text-lg font-bold">{t('sponsorShell.dataUnavailable.title')}</h2>
            </div>
            <p className="m-0 text-[0.95rem] text-ink-muted">{t('sponsorShell.dataUnavailable.body')}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>{t('sponsorCommon.retry')}</Button>
        </Card>
    )
}

export function ListSkeleton({ rows = 2 }: { rows?: number }) {
    const { t } = useTranslation()
    return (
        <div role="status" aria-label={t('sponsorCommon.loading')} className="flex flex-col gap-2.5">
            {Array.from({ length: rows }, (_, i) => (
                <div key={i} className="flex animate-pulse items-center gap-3 rounded-[18px] border border-line bg-surface-warm p-3.5">
                    <div className="size-12 shrink-0 rounded-full bg-line" />
                    <div className="flex grow flex-col gap-2">
                        <div className="h-4 w-28 rounded bg-line" />
                        <div className="h-3.5 w-44 rounded bg-line/70" />
                    </div>
                </div>
            ))}
        </div>
    )
}
